import { useEffect, useMemo, useState } from "react";
import HomeButton from "../components/HomeButton";
import { api, type StaffRow, type StaffRoleGrant } from "../lib/api";
import { normalizeRole, type Role } from "../lib/roles";

/** Роли, которые можно ВЫДАВАТЬ (owner запрещён) */
const GRANT_ROLES: StaffRoleGrant[] = ["door", "admin", "superadmin"];

export default function StaffList() {
  const [loading, setLoading] = useState(true);
  const [meRole, setMeRole] = useState<Role>(null);
  const [items, setItems] = useState<StaffRow[]>([]);
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  // форма добавления
  const [newId, setNewId] = useState("");

  // лист выбора роли (bottom-sheet)
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<null | "edit" | "add">(null);
  const [sheetTarget, setSheetTarget] = useState<StaffRow | null>(null);
  const [sheetIdentifier, setSheetIdentifier] = useState<string>("");

  const canManage = meRole === "owner" || meRole === "superadmin";

  /* ---- me ---- */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await api.me();
        if (!alive) return;
        setMeRole(normalizeRole(me.role));
      } catch {
        setMeRole(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* ---- list ---- */
  async function reload() {
    const rows = await api.staff.list();
    setItems(rows || []);
  }
  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((r) => {
      if (onlyActive && !r.active) return false;
      if (!term) return true;
      const hay = [r.username || "", r.name || ""].join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [items, q, onlyActive]);

  /* ---- actions ---- */
  async function toggleActive(row: StaffRow) {
    if (!canManage || busyId) return;
    if (row.role === "owner") return; // owner нельзя трогать
    setBusyId(row.tg_id);
    try {
      const ok = await api.staff.setActive(row.tg_id, !row.active);
      if (ok.ok) {
        setItems((prev) => prev.map((x) => (x.tg_id === row.tg_id ? { ...x, active: !x.active } : x)));
      }
    } finally {
      setBusyId(null);
    }
  }

  async function changeRole(row: StaffRow, role: StaffRoleGrant) {
    if (!canManage || busyId) return;
    if (row.role === "owner") return; // owner нельзя менять
    setBusyId(row.tg_id);
    try {
      const resp = await api.staff.setRole(row.tg_id, role);
      if (resp.ok) {
        setItems((prev) => prev.map((x) => (x.tg_id === row.tg_id ? { ...x, role } : x)));
      } else {
        alert("Не удалось изменить роль");
      }
    } catch (e) {
      console.error(e);
      alert("Ошибка при изменении роли");
    } finally {
      setBusyId(null);
      setSheetOpen(false);
      setSheetTarget(null);
    }
  }

  async function removeMember(row: StaffRow) {
    if (!canManage || busyId) return;
    if (row.role === "owner") return;
    if (!confirm(`Удалить ${row.username ? "@" + row.username : row.name || row.tg_id} из стаффа?`)) return;

    setBusyId(row.tg_id);
    try {
      const r = await api.staff.remove(row.tg_id);
      if (r.ok) setItems(prev => prev.filter(x => x.tg_id !== row.tg_id));
      else alert("Не удалось удалить пользователя");
    } catch (e) {
      console.error(e);
      alert("Ошибка при удалении");
    } finally {
      setBusyId(null);
    }
  }

  function openEditSheet(row: StaffRow) {
    if (!canManage) return;
    if (row.role === "owner") return;
    setSheetMode("edit");
    setSheetTarget(row);
    setSheetOpen(true);
  }

  function openAddSheet() {
    if (!canManage) return;
    const id = newId.trim();
    if (!id) return;
    setSheetIdentifier(id);
    setSheetMode("add");
    setSheetOpen(true);
  }

  async function onPickRole(r: StaffRoleGrant) {
    if (sheetMode === "edit" && sheetTarget) {
      await changeRole(sheetTarget, r);
      return;
    }
    if (sheetMode === "add" && sheetIdentifier) {
      try {
        const resp = await api.staff.add(sheetIdentifier, r);
        if (resp.ok) {
          await reload();
          setNewId("");
        } else {
          alert("Не удалось выдать роль");
        }
      } catch (e) {
        console.error(e);
        alert("Ошибка при выдаче роли");
      } finally {
        setSheetIdentifier("");
        setSheetOpen(false);
        setSheetMode(null);
      }
    }
  }

  return (
    <div className="ui-card p-4 sm:p-6">
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-[22px] sm:text-[28px] font-extrabold">Состав персонала</h2>
        <div className="flex items-center gap-2">
          <HomeButton />
          <span className="ui-chip hidden sm:inline">
            v1.5
          </span>
        </div>
      </header>

      {/* Добавление стаффа: ввод → кнопка → выбор роли в bottom-sheet */}
      {canManage && (
        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
          <label className="grid gap-1 text-sm">
            <span className="text-neutral-300">Добавить по @username или tg_id</span>
            <input
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              placeholder="@username или 123456789"
              className="ui-input"
            />
          </label>

          <button
            disabled={!newId.trim()}
            onClick={openAddSheet}
            className="ui-btn-sm h-[40px] shrink-0 text-sm font-semibold disabled:opacity-60"
          >
            Добавить
          </button>
        </div>
      )}

      {/* Фильтр по ролям — отключён по твоей просьбе */}

      {/* Поиск + только активные */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-sm">
          <span className="text-neutral-300">Поиск</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="@username, имя…"
            className="ui-input"
          />
        </label>
        <label className="flex items-end gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
            className="h-4 w-4 accent-white/80"
          />
          <span className="text-neutral-300">Только активные</span>
        </label>
      </div>

      {/* MOBILE: карточки */}
      <div className="grid gap-3 sm:hidden">
        {loading && <EmptyRow text="Загружаем…" />}
        {!loading && rows.length === 0 && <EmptyRow text="Ничего не найдено" />}

        {!loading &&
          rows.map((r) => (
            <article key={r.tg_id} className="rounded-2xl border border-white/10 bg-white/7 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm text-white/90">{r.username ? `@${r.username}` : "—"}</div>
                  <div className="truncate text-xs text-white/60">{r.name || "—"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={r.active ? "ok" : "muted"}>{r.active ? "активен" : "отключён"}</Badge>

                  {r.role === "owner" ? (
                    <Badge tone="neutral">owner</Badge>
                  ) : canManage ? (
                    <button
                      disabled={busyId === r.tg_id}
                      onClick={() => openEditSheet(r)}
                      className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {r.role}
                    </button>
                  ) : (
                    <Badge tone="neutral">{r.role}</Badge>
                  )}
                </div>
              </div>

              {canManage && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    disabled={busyId === r.tg_id || r.role === "owner"}
                    onClick={() => toggleActive(r)}
                    className="rounded-xl border border-white/12 bg-white/[.06] px-3 py-2 text-xs font-semibold text-white hover:bg-white/[.10] disabled:opacity-60"
                  >
                    {r.active ? "Отключить" : "Включить"}
                  </button>
                  <button
                    disabled={busyId === r.tg_id || r.role === "owner"}
                    onClick={() => removeMember(r)}
                    className="rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs font-semibold text-red-100 hover:bg-red-400/15 disabled:opacity-60"
                  >
                    Удалить
                  </button>
                </div>
              )}
            </article>
          ))}
      </div>

      {/* DESKTOP/TABLET: таблица */}
      <div className="hidden sm:block overflow-x-auto rounded-2xl border border-white/10">
        <table className="min-w-full text-sm text-white/90">
          <thead className="bg-white/10 text-xs uppercase text-white/70">
            <tr className="text-center">
              <Th>Username</Th>
              <Th>Имя</Th>
              <Th>Роль</Th>
              <Th>Статус</Th>
              <Th>Действия</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-white/70">Загружаем…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-white/60">Ничего не найдено</td></tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr key={r.tg_id} className="border-t border-white/10 text-center hover:bg-white/5">
                  <Td>{r.username ? `@${r.username}` : <span className="text-white/50">—</span>}</Td>
                  <Td>{r.name || <span className="text-white/50">—</span>}</Td>
                  <Td>
                    {r.role === "owner" ? (
                      <Badge tone="neutral">owner</Badge>
                    ) : canManage ? (
                      <button
                        disabled={busyId === r.tg_id}
                        onClick={() => openEditSheet(r)}
                        className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                      >
                        {r.role}
                      </button>
                    ) : (
                      <Badge tone="neutral">{r.role}</Badge>
                    )}
                  </Td>
                  <Td><Badge tone={r.active ? "ok" : "muted"}>{r.active ? "активен" : "отключён"}</Badge></Td>
                  <Td>
                    {canManage ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          disabled={busyId === r.tg_id || r.role === "owner"}
                          onClick={() => toggleActive(r)}
                          className="rounded-xl border border-white/12 bg-white/[.06] px-3 py-2 text-xs font-semibold text-white hover:bg-white/[.10] disabled:opacity-60"
                        >
                          {r.active ? "Отключить" : "Включить"}
                        </button>
                        <button
                          disabled={busyId === r.tg_id || r.role === "owner"}
                          onClick={() => removeMember(r)}
                          className="rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs font-semibold text-red-100 hover:bg-red-400/15 disabled:opacity-60"
                        >
                          Удалить
                        </button>
                      </div>
                    ) : (
                      <span className="text-white/50 text-xs">нет доступа</span>
                    )}
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ---- Bottom sheet: выбор роли ---- */}
      <RoleSheet
        open={sheetOpen}
        title={
          sheetMode === "edit"
            ? `Изменить роль для ${sheetTarget?.username ? "@" + sheetTarget.username : sheetTarget?.name || ""}`
            : sheetMode === "add"
            ? `Выдать роль пользователю ${sheetIdentifier}`
            : ""
        }
        onClose={() => {
          setSheetOpen(false);
          setSheetMode(null);
          setSheetTarget(null);
          setSheetIdentifier("");
        }}
        onPick={onPickRole}
      />
    </div>
  );
}

/* ---------- Bottom Sheet ---------- */
function RoleSheet({
  open, title, onClose, onPick,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onPick: (r: StaffRoleGrant) => void;
}) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl border-t border-white/10 bg-[#0b1220]/95 p-4 pb-6">
        <div className="mx-auto mb-3 h-1.5 w-20 rounded-full bg-white/15" />
        <div className="mx-auto max-w-[640px]">
          {!!title && <div className="mb-3 text-center text-sm text-white/80">{title}</div>}
          <div className="grid grid-cols-2 gap-3">
            {GRANT_ROLES.map((r) => (
              <button
                key={r}
                onClick={() => onPick(r)}
                className="rounded-2xl border border-white/15 bg-white/[.07] px-4 py-3 text-base font-semibold text-white hover:bg-white/[.12]"
              >
                {r}
              </button>
            ))}
            <button disabled className="rounded-2xl border border-white/10 bg-white/[.04] px-4 py-3 text-base font-semibold text-white/40" title="Роль owner нельзя выдавать">
              owner
            </button>
            <button onClick={onClose} className="col-span-2 rounded-2xl border border-white/12 bg-white/[.06] px-4 py-3 text-base font-semibold text-white hover:bg-white/[.10]">
              Отмена
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- UI helpers ---------- */
function EmptyRow({ text }: { text: string }) {
  return <div className="rounded-xl border border-white/10 bg-white/7 p-3 text-center text-white/70">{text}</div>;
}
function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-semibold tracking-wide text-center">{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2 align-middle text-center">{children}</td>;
}
function Badge({ tone = "neutral", children }: { tone?: "ok" | "muted" | "neutral"; children: React.ReactNode }) {
  const styles =
    tone === "ok"
      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100"
      : tone === "muted"
      ? "border-white/15 bg-white/5 text-white/60"
      : "border-white/15 bg-white/10 text-white/90";
  return <span className={`inline-flex items-center justify-center rounded-full border px-2 py-[2px] text-[11px] ${styles}`}>{children}</span>;
}
